import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { investmentHoldingsV2Selectors } from 'app-components/flux/investment-holdings-v2';
import { getEnvironmentConfig, httpClientInterface } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

const types = ['BUY', 'BUY', 'BUY', 'SELL', 'SELL', 'CASH_FLOW'];

const cpTypes = {
  BUY: 'BUYOTHER',
  SELL: 'SOLD',
} as const;

type InvestmentTxnType = keyof typeof cpTypes;

type BaseTransactionProps = {
  postedOn: string;
  amount: number;
  units: number;
  symbol: string;
  investmentTxnType: InvestmentTxnType;
  payee: string;
  [key: string]: any;
};

const baseTransaction = (props: BaseTransactionProps) => ({
  clientId: uuidv4().toUpperCase(),
  isExcludedFromReports: true,
  isExcludedFromF2S: true,
  type: 'INVESTMENT',
  memo: '',
  coa: {
    type: 'UNCATEGORIZED',
    id: '0',
  },
  state: 'CLEARED',
  matchState: 'NOT_MATCHED',
  source: 'QCS_REFRESH',
  cpData: {
    id: uuidv4().toUpperCase(),
    txnType: cpTypes[props.investmentTxnType],
    postedOn: props.postedOn,
    txnOn: props.postedOn,
    payee: `${props.payee}`,
    amount: props.amount,
    inferredPayee: props.payee,
    inferredCoa: {
      type: 'UNCATEGORIZED',
      id: '0',
    },
    cpCategoryId: '20',
    accruedInterest: 0,
    commission: 0,
    fees: 0,
    investmentTxnType: cpTypes[props.investmentTxnType],
    securityType: 'STOCK',
    symbol: props.symbol,
    units: props.units,
  },
  ...props,
});

const mockInvestmentTransactions = async () => {
  const startDate = DateTime.fromISO('2024-01-01');
  const endDate = DateTime.now();
  const daysBetween = endDate.diff(startDate, 'days').days;
  const transactions: any[] = [];
  const requestUrl = `${getEnvironmentConfig().services_url}/v2/investments/historical-quotes`;

  if (store) {
    const holdings = investmentHoldingsV2Selectors.getResourcesById(store.getState());
    for (const holding of holdings.toIndexedSeq()) {
      const numberOfTransactions = Math.floor(Math.random() * 3);
      for (let i = 0; i < numberOfTransactions; i += 1) {
        const type = types[Math.floor(Math.random() * types.length)];
        const postedOn = startDate.plus({ days: Math.floor(Math.random() * daysBetween) }).toISODate();
        if (type === 'CASH_FLOW') {
          const amount = Math.round(((0.5 - Math.random()) * 2 * 4000 + 20) * 100) / 100;
          const payee = amount < 0 ? 'Transfer Sent (cash)' : 'Transfer Received (cash)';
          transactions.push({
            type: 'CASH_FLOW',
            clientId: uuidv4().toUpperCase(),
            accountId: holding.accountId,
            postedOn,
            payee,
            amount,
            state: 'CLEARED',
            matchState: 'NOT_MATCHED',
            coa: {
              type: 'BALANCE_ADJUSTMENT',
              id: '2',
            },
            cpData: {
              id: uuidv4().toUpperCase(),
              txnType: amount < 0 ? 'WITHDRAWAL' : 'DEPOSIT',
              postedOn,
              txnOn: postedOn,
              payee: 'Electronic Funds Transfer (Cash)',
              amount,
              inferredPayee: payee,
              inferredCoa: {
                type: 'BALANCE_ADJUSTMENT',
                id: '2',
              },
              cpCategoryId: '21',
            },
            source: 'QCS_REFRESH',
            isExcludedFromReports: true,
            isExcludedFromF2S: true,
          });
        } else {
          const { symbol } = holding;
          let response: any;
          try {
            response = await httpClientInterface.send('GET', requestUrl, undefined, undefined, {
              symbol,
              startDate: postedOn,
              endDate: postedOn,
              intervalType: 'DAILY',
              splits: false,
            });
          } catch (_e) {
            // Intentionally ignore errors for mock data generation
          }
          const quote = response?.data?.resources?.[0];
          if (quote?.close && postedOn && symbol) {
            const units = Math.floor(Math.random() * (holding.quantity || 1 - 1)) + 1;
            const amount = units * quote.close;
            switch (type) {
              case 'BUY':
                transactions.push({
                  ...baseTransaction({
                    postedOn,
                    amount,
                    units,
                    symbol,
                    investmentTxnType: 'BUY',
                    payee: `You Bought ${symbol}`,
                  }),
                  accountId: holding.accountId,
                  localSecurityId: holding.localSecurityId,
                });
                break;
              case 'SELL':
                transactions.push({
                  ...baseTransaction({
                    postedOn,
                    amount,
                    units,
                    symbol,
                    investmentTxnType: 'SELL',
                    payee: `You Sold ${symbol}`,
                  }),
                  accountId: holding.accountId,
                  localSecurityId: holding.localSecurityId,
                });
                break;
            }
          }
        }
      }
    }

    return transactions;
  }

  return [];
};

export default mockInvestmentTransactions;
